// Mit eingeschalteten Stufenbranches lehnt der Server den bisherigen Transportweg mit STAGE_BRANCHES_ACTIVE ab
// (Vertrag 24.1). Eine Textquelle für alle Oberflächen: freigabe.json, stageBranches.errors.STAGE_BRANCHES_ACTIVE.
import de from "../i18n/de/freigabe.json";
import en from "../i18n/en/freigabe.json";
import { stageLabel } from "../utils/stages";
import { parseTargetKey } from "../utils/stageTargets";

export const STAGE_BRANCHES_ACTIVE = "STAGE_BRANCHES_ACTIVE";

export const isStageBranchesActive = error => (typeof error === "string" ? error : error?.code || error?.body?.code) === STAGE_BRANCHES_ACTIVE;

const catalog = language => (String(language || "de").toLowerCase().startsWith("en") ? en : de);

export const stageBranchesActiveText = (language = "de") => catalog(language).stageBranches.errors.STAGE_BRANCHES_ACTIVE;

// Platzhalter {{name}} aus den details der Antwort füllen (Vertrag 26.5). Wie errors.js auf dem Server: nur wenn
// details jeden Wert trägt, sonst bleibt der Text unverändert.
const fill = (text, details) => {
  const keys = [...String(text).matchAll(/\{\{(\w+)\}\}/g)].map(match => match[1]);
  if (!keys.length || !details || keys.some(key => details[key] == null)) return String(text);
  return String(text).replace(/\{\{(\w+)\}\}/g, (_, key) => String(details[key]));
};

// Text eines bekannten Stufenbranch-Codes (Vertrag 15, stageBranches.errors), sonst "".
// Topic inzwischen gelöscht oder archiviert (STAGE_INPUT_INVALID mit details.reason, Vertrag 26.1, 26.3): eigener Satz,
// der Servercode bleibt.
const TOPIC_GONE = new Set(["topic-unknown", "topic-archived"]);

export function stageBranchesErrorText(error, language = "de") {
  const code = typeof error === "string" ? error : error?.code || error?.body?.code;
  const reason = typeof error === "object" ? (error?.details || error?.body?.details)?.reason : null;
  if (code === "STAGE_INPUT_INVALID" && TOPIC_GONE.has(reason)) return catalog(language).stageBranches.topicChange.topicGone;
  if (code === "STAGE_JOURNAL_UNAVAILABLE") {
    const reasons = catalog(language).stageBranches.storageUnavailableReasons;
    if (Object.prototype.hasOwnProperty.call(reasons, reason)) return reasons[reason];
  }
  const texts = catalog(language).stageBranches.errors;
  if (typeof code !== "string" || !Object.prototype.hasOwnProperty.call(texts, code)) return "";
  return fill(texts[code], typeof error === "object" ? error?.details || error?.body?.details : null);
}

// Mehrere Ziele je Stufe (Integration I1): Codes mit betroffenen Stufen (details.stages) oder Zielen (details.targets bzw.
// beim Snapshot targets in der Antwort). Die Liste steht als eigener Satz unter dem Text des Codes, damit sichtbar ist, welche
// Stufe oder welches Ziel gemeint ist. targetName(stage, targetId) liefert den Instanznamen, sonst steht Stufe und Ziel-ID.
const LIST_CODES = new Set(["STAGE_TARGET_INVALID", "STAGE_TARGET_ALREADY_REACHED", "STAGE_MULTI_TARGET_DISABLED",
  "STAGE_DEFAULT_TARGET_REQUIRED", "STAGE_TARGET_SNAPSHOT_REQUIRED"]);

function targetLabel(value, texts, targetName) {
  const text = String(value);
  const parsed = text.includes("@") || /^(dev|qas|preprod|prod)$/.test(text) ? parseTargetKey(text) : null;
  const stage = parsed?.stage ?? null, targetId = parsed ? parsed.targetId : text;
  const name = typeof targetName === "function" ? targetName(stage, targetId) : null;
  if (name) return name;
  if (targetId === "default") return stage ? `${stageLabel(stage)} (${texts.default})` : texts.default;
  return stage ? `${stageLabel(stage)} ${targetId}` : targetId;
}

export function stageBranchesErrorDetail(error, { language = "de", targetName } = {}) {
  const code = typeof error === "string" ? error : error?.code || error?.body?.code;
  if (!LIST_CODES.has(code) || typeof error !== "object") return "";
  const details = error.details || error.body?.details || {};
  const texts = catalog(language).stageBranches.targets;
  const stages = Array.isArray(details.stages) ? details.stages.filter(item => typeof item === "string") : [];
  const targets = ([details.targets, error.targets, error.body?.targets].find(Array.isArray) || []).filter(item => typeof item === "string");
  const parts = [];
  if (stages.length) parts.push(fill(texts.affectedStages, { stages: [...new Set(stages)].map(stageLabel).join(", ") }));
  if (targets.length) parts.push(fill(texts.affectedTargets, { targets: [...new Set(targets)].map(item => targetLabel(item, texts, targetName)).join(", ") }));
  return parts.join(" ");
}
